import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import moment from "moment";
import PaymentMode from "../../../components/modals/paymentMode";
import UPIPaymentModal from "../../../components/modals/upiPaymentModal";
import ConfirmaTransaction from "../../../components/modals/confirmTransaction";
import ModalComponent from "../../../components/modals/modalComponent";
import walletServices from "../../../services/walletServices";
import logsService from "../../../services/logServices";
import InvestorActivityEventType from "../../../utils/eventTypes";

const BANK_TRANSFER_THRESHOLD = 100000;

function openRazorpayCheckout({ order, user, onSuccess }) {
  const options = {
    key: order.notes.key,
    amount: order.amount,
    currency: "INR",
    name: user?.name || user?.username || "Investor",
    description: "Add funds to wallet",
    order_id: order.id,
    handler: onSuccess,
    prefill: {
      name: user?.name || user?.username,
      email: user?.email || user?.emailId,
      contact: user?.number || user?.mobileNumber,
    },
    notes: { address: "REALX" },
    theme: { color: "#2F6BF2" },
    modal: {
      escape: false,
      confirm_close: true,
    },
  };

  const razor = new window.Razorpay(options);
  razor.on("payment.failed", () => { });
  razor.open();
}

export default function AddFundsModal({ open, onClose, onSuccess, locked = false }) {
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state) => state.user.data);

  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("amount");
  const [submitting, setSubmitting] = useState(false);
  const [walletTransactionId, setWalletTransactionId] = useState(null);

  const numericAmount = Number(amount);
  const isValidAmount = Number.isFinite(numericAmount) && numericAmount > 0;

  useEffect(() => {
    if (!open) {
      setAmount("");
      setStep("amount");
      setSubmitting(false);
    }
  }, [open]);

  const handleCloseAll = () => {
    onClose();
  };

  const handleContinueAmount = () => {
    if (locked) {
      enqueueSnackbar("Wallet is locked", { variant: "error" });
      return;
    }
    if (!isValidAmount) {
      enqueueSnackbar("Enter a valid amount", { variant: "error" });
      return;
    }
    if (numericAmount <= BANK_TRANSFER_THRESHOLD) {
      setStep("mode");
    } else {
      setStep("bank");
    }
  };

  const handleSelectUPI = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await handleUPIInitiate();
      setStep("upi");
    } catch (error) {
      enqueueSnackbar("Unable to proceed with UPI currently, Please try again after some time!", { variant: "error", anchorOrigin: { horizontal: "center", vertical: "bottom" }, autoHideDuration: 2000 })
    } finally {
      setSubmitting(false);
    }
  }

  const handleUPIInitiate = async () => {
    const res = await walletServices.initiateUPI({ amount })
    if (res && res.success) {
      // alert(res.data?.entryId);
      setWalletTransactionId(res.data?.entryId);
      // TODO: add event of UPI here
    }
    else enqueueSnackbar(res?.data?.message || res?.message || "Something went wrong", { variant: "error", anchorOrigin: { horizontal: "center", vertical: "bottom" }, autoHideDuration: 2000 })
  }

  const handleUPISubmit = async () => {
    const res = await walletServices.depositUPI({ walletTransactionId })
    if (res && res.success) {
      enqueueSnackbar("Payment successful.", { variant: "success", anchorOrigin: { horizontal: "center", vertical: "bottom" }, autoHideDuration: 2000 })
      // TODO: add event of UPI here
    }
    else {
      enqueueSnackbar(res?.data?.message || res?.message || "Something went wrong", { variant: "error", anchorOrigin: { horizontal: "center", vertical: "bottom" }, autoHideDuration: 2000 })
    }
  }

  const handlePaymentGateway = async () => {
    setSubmitting(true);
    const res = await walletServices.createDepositOrder({ amount: numericAmount, userId: user?._id });
    setSubmitting(false);

    if (!res || res?.success === false) {
      enqueueSnackbar(res?.message || res?.data?.message || "Failed to create payment order", {
        variant: "error",
      });
      return;
    }

    const order = res.data
    if (!order?.id) {
      enqueueSnackbar("Invalid payment order response", { variant: "error" });
      return;
    }

    openRazorpayCheckout({
      order,
      user,
      onSuccess: async () => {
        enqueueSnackbar("Payment successful. Wallet will be updated shortly.", { variant: "success" });
        handleCloseAll();
        logsService.addLog({
          userId: user?._id,
          event: InvestorActivityEventType.GATEWAY_DEPOSIT_SUCCESSFUL
        })
        await onSuccess?.();
      },
    });
  };

  return (
    <>
      <ModalComponent
        open={open && step === "amount"}
        handleClose={handleCloseAll}
        title="Add funds"
        maxWidth="sm"
        fullWidth
      >
        <Stack spacing={2.5} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Add money to your wallet via your preferred payment option.
          </Typography>
          <TextField
            fullWidth
            label="Amount (INR)"
            value={amount}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (RegExp('^[0-9]*$').test(inputValue) && inputValue.length <= 16) {
                setAmount(inputValue);
              }
            }}
            inputMode="numeric"
            placeholder="e.g. 50000"
          // helperText={
          //   numericAmount > BANK_TRANSFER_THRESHOLD
          //     ? "Amounts above ₹80,000 require bank transfer."
          //     : "Amounts up to ₹80,000 can use payment gateway."
          // }
          />
          <Box sx={{ px: { xs: 0, md: 4 } }}>
            <Button
              fullWidth
              variant="contained"
              disabled={!isValidAmount || locked || submitting}
              onClick={handleContinueAmount}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Continue
            </Button>
          </Box>
        </Stack>
      </ModalComponent>

      {step === "mode" && <PaymentMode
        open={open}
        handleClose={handleCloseAll}
        onBankTransfer={() => setStep("bank")}
        onPaymentGateway={handlePaymentGateway}
        onUPI={handleSelectUPI}
        isUPIEnabled={true}
        walletPayAmount={amount}
        onWalletPay={false}
        loading={submitting}
      />}

      {step === "upi" && (
        <UPIPaymentModal
          open={open}
          handleClose={handleCloseAll}
          amount={numericAmount}
          transactionRef={walletTransactionId}
          onSubmit={handleUPISubmit}
        />
      )}

      {step === "bank" && (
        <ConfirmaTransaction
          open={open}
          handleClose={() => setStep(numericAmount <= BANK_TRANSFER_THRESHOLD ? "mode" : "amount")}
          amount={numericAmount}
          amtTobePaid={numericAmount}
          amtPaid={0}
          commitmentDate={moment()}
          entityId={user?._id}
          entityType="PERSONAL"
          data={{}}
          showAccInformation
          hideNavigate
          footerText="RealX will verify the payment and the amount will be credited to your wallet."
          onSubmit={(payload) => {
            const info = payload?.banktransferInfo || {};
            logsService.addLog({
              userId: user?._id,
              event: InvestorActivityEventType.BANK_DEPOSIT_INITIATED
            })
            return walletServices.depositBankTransfer({
              amount: info.amountTransferred ?? numericAmount,
              bankAccountId: info.bankAccountId,
              chequeNo: info.chequeno,
              dateOfTransaction: info.dateoftrans,
            });
          }}
          onSuccess={async () => {
            handleCloseAll();
            await onSuccess?.();
          }}
        />
      )}
    </>
  );
}
